import type { PassportLevel } from "../../services/api/travelHealth";
import type { Translation } from "../../types/i18n";

/**
 * How a passport stamp looks (UX_CONCEPT.md §7: "styled by city"). Shape and
 * colour follow the stamp's place in the passport — the first stamp earned is
 * always the first look — so neighbours never match; the tilt follows the
 * city's name.
 */
export interface StampLook {
  shape: "round" | "rect";
  /** Text and border colour classes. */
  tone: string;
  /** Degrees; a hand-pressed stamp is never straight. */
  rotate: number;
}

const LOOKS: ReadonlyArray<Pick<StampLook, "shape" | "tone">> = [
  { shape: "round", tone: "text-primary-600 border-primary-600 border-solid" },
  { shape: "rect", tone: "text-travel-700 border-travel-700 border-double" },
  { shape: "round", tone: "text-health-700 border-health-700 border-solid" },
  { shape: "rect", tone: "text-primary-600 border-primary-600 border-double" },
  { shape: "round", tone: "text-travel-700 border-travel-700 border-solid" },
];

const ROTATIONS = [-8, 5, -3, 7, -11, 3];

function hash(text: string): number {
  let h = 0;
  for (const ch of text.toUpperCase()) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h;
}

/** `ordinal` is 1 for the first stamp earned; 0 for one not yet earned. */
export function stampLook(city: string, ordinal: number): StampLook {
  return {
    ...LOOKS[Math.max(ordinal - 1, 0) % LOOKS.length]!,
    rotate: ROTATIONS[hash(city) % ROTATIONS.length]!,
  };
}

/** "LISBON"; long names are cut so they fit the stamp. */
export function stampCity(city: string): string {
  const upper = city.trim().toUpperCase();
  return upper.length > 10 ? `${upper.slice(0, 9)}…` : upper;
}

/** "OCT 26" — the month and year of the trip, as passports print it. */
export function stampDate(iso: string | null, locale = "en"): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const month = date
    .toLocaleDateString(locale, { month: "short", timeZone: "UTC" })
    .replace(".", "")
    .toUpperCase();
  return `${month} ${String(date.getUTCFullYear()).slice(-2)}`;
}

export function levelName(level: PassportLevel, t: Translation): string {
  switch (level) {
    case "starter":
      return t.levelStarter;
    case "explorer":
      return t.levelExplorer;
    case "globetrotter":
      return t.levelGlobetrotter;
    case "nomad":
      return t.levelNomad;
  }
}
